import {
  faCode,
  faGlobe,
  faKey,
  faPlug,
  faShieldHalved,
  faUsers,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  createFileRoute,
  Outlet,
  redirect,
  useRouterState,
} from '@tanstack/react-router';
import { CompanySettingsHeader } from '@/app/(client)/company-settings/-components/CompanySettingsHeader';
import { PermissionSetsProvider } from '@/app/(client)/company-settings/-components/permissionSets/PermissionSetsProvider';
import type { TabEntry } from '@/components/shared/PageTabs';
import PageTabs from '@/components/shared/PageTabs';
import { usePermissions } from '@/hooks/usePermissions';
import { useTranslator } from '@/hooks/useTranslator';
import { hasFeature } from '@/lib/utils/features';
import { useSessionStore } from '@/store/sessionStore';

const BASE = '/company-settings';

export const Route = createFileRoute('/(client)/company-settings/(pages)')({
  beforeLoad: ({ location }) => {
    if (
      location.pathname === '/company-settings' ||
      location.pathname === '/company-settings/'
    ) {
      throw redirect({ to: '/company-settings/users' });
    }
  },
  component: CompanySettingsLayout,
});

function CompanySettingsLayout() {
  const { getTranslation: t } = useTranslator({ page: 'companyDetail' });
  const { getTranslation: tApiKeys } = useTranslator({ page: 'apiKeys' });
  const { getTranslation: tSidebar } = useTranslator({ page: 'sidebar' });
  const { getTranslation: tConstantsMapping } = useTranslator({
    page: 'constantsMapping',
  });
  const { getTranslation: tShared } = useTranslator({ page: 'shared' });
  const { getTranslation: tProfile } = useTranslator({ page: 'profile' });
  const user = useSessionStore(s => s.user);
  const company = user?.company;

  // Permission sets ship to every company — the model itself is not sold, only
  // the authoring of CUSTOM sets is. So the tab is gated on what the caller may
  // DO with the model (`permission-access:read`), not on `user:read`, which is
  // about team members. Same rule the company profile used before this moved.
  const permissions = usePermissions();
  const canViewPermissions = permissions['permission-access'].canRead;

  // Moved off the sidebar's "Developers' HQ" — the same rule the route's own
  // guard uses: the feature says the company bought self-service keys,
  // `api-key:read` says this caller may see the list.
  const canViewApiKeys =
    hasFeature(user, 'self-service-api-key') && permissions['api-key'].canRead;

  // Same move, same family, its own former "Developers' HQ" sibling — the
  // feature says the company bought constants mapping, `read` says this
  // caller may look at it. Mind the two spellings: `constants-mapping` is
  // the platform feature, `constant-mapping` the IAM resource.
  const canViewConstantsMapping =
    hasFeature(user, 'constants-mapping') &&
    permissions['constant-mapping'].canRead;

  const pathname = useRouterState({ select: s => s.location.pathname });

  const tabs: TabEntry[] = [
    {
      key: 'users',
      label: t('tabs.users'),
      icon: <FontAwesomeIcon icon={faUsers} />,
      to: `${BASE}/users`,
    },
    ...(canViewPermissions
      ? [
          {
            key: 'permission-sets',
            label: t('tabs.permissionSets'),
            icon: <FontAwesomeIcon icon={faShieldHalved} />,
            to: `${BASE}/permission-sets`,
          } as TabEntry,
        ]
      : []),
    {
      key: 'integrations',
      label: t('tabs.integrations'),
      icon: <FontAwesomeIcon icon={faPlug} />,
      to: `${BASE}/integrations`,
    },
    {
      key: 'webshop-settings',
      label: tProfile('settings.webshop.title'),
      icon: <FontAwesomeIcon icon={faGlobe} />,
      to: `${BASE}/webshop-settings`,
    },
    ...(canViewApiKeys
      ? [
          {
            key: 'api-keys',
            label: tApiKeys('list.title'),
            icon: <FontAwesomeIcon icon={faKey} />,
            to: `${BASE}/api-keys`,
          } as TabEntry,
        ]
      : []),
    // A dropdown, not a plain tab: its three destinations are siblings
    // (materials/product-types/facility-processes), not one page with a default —
    // and a plain `FlatTab.to` only matches by path prefix, so it could only
    // ever highlight for one of the three. A dropdown's children all feed
    // the same active check (`NestedDropdownPill`'s `anyActive`), so the
    // pill stays lit across all three, and `showActiveChild` names which one
    // — replacing the section's own former tab row, which this now is.
    ...(canViewConstantsMapping
      ? [
          {
            type: 'dropdown' as const,
            key: 'constants-mapping',
            label: tSidebar('constantsMapping'),
            icon: <FontAwesomeIcon icon={faCode} />,
            showActiveChild: true,
            children: [
              {
                key: 'materials',
                label: tConstantsMapping('tabs.materials'),
                to: `${BASE}/constants-mapping/materials`,
              },
              {
                key: 'product-types',
                label: tConstantsMapping('tabs.productTypes'),
                to: `${BASE}/constants-mapping/product-types`,
              },
              {
                key: 'facility-processes',
                label: tConstantsMapping('tabs.facilityProcesses'),
                to: `${BASE}/constants-mapping/facility-processes`,
              },
            ],
          } as TabEntry,
        ]
      : []),
  ];

  if (!company) return null;

  return (
    // Wraps the whole layout, not one tab — a set assigned on Users has to
    // still be there when Permission sets is opened, and a set created in the
    // builder has to survive the way back to the list. Same reasoning the
    // company profile's own wrapping used; only the `enabled` paths changed,
    // since both screens now live under this layout instead.
    <PermissionSetsProvider
      companyId={company.id}
      enabled={
        pathname.startsWith(`${BASE}/users`) ||
        pathname.startsWith(`${BASE}/permission-sets`)
      }
    >
      <div className='flex flex-col flex-1 min-h-0 overflow-y-auto'>
        <CompanySettingsHeader company={company} />
        <div className='sticky top-0 z-20 bg-background'>
          <PageTabs tabs={tabs} mode='path' moreLabel={tShared('moreTabs')} />
        </div>
        <div className='flex flex-col flex-1 min-h-[70dvh]'>
          <Outlet />
        </div>
      </div>
    </PermissionSetsProvider>
  );
}
